/* Curated homepage presentations. No cookies, network calls or customer identity. */
(() => {
  'use strict';
  if (customElements.get('lc-home-rotation')) return;
  const editorSelections = new Map();
  const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const parse = (value) => {
    try { return JSON.parse(value || 'null'); } catch { return null; }
  };

  class LoveCashmereHomeRotation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.panels = [...this.querySelectorAll('[data-presentation]')];
      if (!this.panels.length) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.isEditor = this.dataset.editor === 'true' || Boolean(window.Shopify?.designMode);
      this.keys = this.panels.filter(panel => panel.dataset.enabled !== 'false').map(panel => panel.dataset.presentation);
      this.defaultKey = this.keys.includes(this.dataset.default) ? this.dataset.default : this.keys[0];
      this.storageKey = this.dataset.storageKey;
      this.signature = [this.dataset.mode, this.defaultKey, this.dataset.weekStart, this.dataset.remember, ...this.keys].join('|');
      const key = this.choose();
      this.show(key);
      document.addEventListener('lc:visit-change', () => {
        if (!this.isEditor) this.show(this.choose());
      }, { signal });

      document.addEventListener('shopify:block:select', event => {
        if (!this.isEditor || String(event.detail?.sectionId) !== this.dataset.sectionId) return;
        const selected = this.panels.find(panel => panel.dataset.blockId === String(event.detail?.blockId));
        if (selected) {
          editorSelections.set(this.dataset.sectionId, { key: selected.dataset.presentation, preview: this.dataset.editorPreview });
          this.show(selected.dataset.presentation);
        }
      }, { signal });


    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    weeklyKey(now) {
      const value = this.dataset.weekStart || '';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return this.defaultKey;
      const date = new Date(`${value}T00:00:00.000Z`);
      const start = date.getTime();
      if (!Number.isFinite(start) || date.toISOString().slice(0, 10) !== value || date.getUTCDay() !== 1 || now < start) return this.defaultKey;
      const weeks = Math.floor((now - start) / WEEK_MS);
      return this.keys[(this.keys.indexOf(this.defaultKey) + weeks) % this.keys.length];
    }

    choose() {
      if (this.isEditor) {
        const selected = editorSelections.get(this.dataset.sectionId);
        if (selected && selected.preview === this.dataset.editorPreview && this.panels.some(panel => panel.dataset.presentation === selected.key)) return selected.key;
        return this.defaultKey;
      }
      if (this.dataset.mode === 'off' || !this.keys.length) return this.defaultKey;
      if (this.dataset.pin && this.dataset.pin !== 'auto') {
        return this.keys.includes(this.dataset.pin) ? this.dataset.pin : this.defaultKey;
      }
      const service = window.LoveCashmereVisitRotation;
      if (this.dataset.mode === 'visits') {
        if (this.dataset.remember !== 'true') return this.defaultKey;
        return service?.choose(`home:${this.storageKey}`, this.keys, this.defaultKey) || this.defaultKey;
      }
      if (this.dataset.mode !== 'weekly') return this.defaultKey;
      const visit = service?.getVisit();
      if (!visit?.persistent) return this.defaultKey;
      try {
        const saved = parse(window.sessionStorage.getItem(this.storageKey));
        if (saved?.signature === this.signature && saved.visitId === visit.id && this.keys.includes(saved.key)) return saved.key;
        const key = this.weeklyKey(Date.now());
        window.sessionStorage.setItem(this.storageKey, JSON.stringify({ signature: this.signature, visitId: visit.id, key }));
        return key;
      } catch {
        return this.defaultKey;
      }
    }

    show(key) {
      const selected = this.panels.find(panel => panel.dataset.presentation === key);
      if (!selected) return;
      for (const panel of this.panels) {
        const active = panel === selected;
        panel.hidden = !active;
        panel.inert = !active;
        panel.querySelector('lc-category-slideshow')?.setAttribute('data-presentation-active', String(active));
        if (active) panel.removeAttribute('aria-hidden');
        else panel.setAttribute('aria-hidden', 'true');
      }
      const image = selected.querySelector('[data-rotation-src]');
      if (image) {
        if (image.dataset.rotationSrcset) image.srcset = image.dataset.rotationSrcset;
        image.src = image.dataset.rotationSrc;
        image.removeAttribute('data-rotation-src');
        image.removeAttribute('data-rotation-srcset');
      }
      this.currentKey = key;
      this.dataset.current = key;
    }
  }

  class LoveCashmereCategorySlideshow extends HTMLElement {
    static get observedAttributes() { return ['data-presentation-active']; }

    connectedCallback() {
      if (this.controller) return;
      this.primary = this.querySelector('.lc-home-rotation__image');
      this.nextImage = this.querySelector('.lc-category-slideshow__next');
      this.caption = this.querySelector('[data-lc-slide-title]');
      this.nextCaption = this.querySelector('[data-lc-slide-title-next]');
      const entries = parse(this.querySelector('[data-lc-category-slides]')?.textContent);
      const seen = new Set();
      this.slides = (Array.isArray(entries) ? entries : []).filter(slide => {
        if (!slide || typeof slide.src !== 'string' || !slide.src || seen.has(slide.src)) return false;
        seen.add(slide.src);
        return true;
      });
      this.updateTitle(this.slides[0]);
      if (this.dataset.enabled === 'false' || !this.primary || !this.nextImage || this.slides.length < 2 || window.Shopify?.designMode) return;
      this.nextImage.hidden = false;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.index = 0;
      this.failed = new Set();
      this.generation = 0;
      this.visible = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.interval = Math.max(8000, Number(this.dataset.interval) || 8000);
      document.addEventListener('visibilitychange', () => this.refresh(), { signal });
      this.motion.addEventListener('change', () => this.refresh(), { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries.some(entry => entry.isIntersecting);
        this.refresh();
      });
      this.observer.observe(this);
      this.refresh();
    }

    attributeChangedCallback() { if (this.controller) this.refresh(); }

    disconnectedCallback() {
      this.cancel();
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }

    canRun() {
      return this.isConnected && this.dataset.presentationActive === 'true' && this.visible &&
        !document.hidden && !this.motion.matches && this.failed.size < this.slides.length - 1;
    }

    refresh() {
      this.cancel();
      if (this.canRun()) this.timer = setTimeout(() => this.advance(), this.interval);
    }

    cancel() {
      clearTimeout(this.timer);
      clearTimeout(this.fadeTimer);
      clearTimeout(this.loadTimer);
      this.generation = (this.generation || 0) + 1;
      if (this.pendingImage) {
        this.pendingImage.onload = this.pendingImage.onerror = null;
        this.pendingImage = null;
      }
      // Finish a loaded transition before pausing so neither image flashes away.
      if (this.fadingIndex !== undefined) this.commit();
      this.classList.remove('lc-category-slideshow--fading');
    }

    applyImage(image, slide) {
      if (slide.srcset) image.srcset = slide.srcset;
      else image.removeAttribute('srcset');
      image.src = slide.src;
      image.alt = slide.alt || '';
    }

    updateTitle(slide) {
      if (this.caption) this.caption.textContent = slide?.title || '';
    }

    commit() {
      this.index = this.fadingIndex;
      this.applyImage(this.primary, this.slides[this.index]);
      this.updateTitle(this.slides[this.index]);
      this.fadingIndex = undefined;
      this.classList.remove('lc-category-slideshow--fading');
    }

    async advance() {
      if (!this.canRun()) return;
      const generation = this.generation;
      let candidate = this.index;
      for (let attempts = 0; attempts < this.slides.length - 1; attempts++) {
        candidate = (candidate + 1) % this.slides.length;
        if (this.failed.has(candidate)) continue;
        const slide = this.slides[candidate];
        const preload = new Image();
        preload.sizes = this.primary.sizes || '100vw';
        this.pendingImage = preload;
        try {
          await new Promise((resolve, reject) => {
            preload.onload = () => { clearTimeout(this.loadTimer); resolve(); };
            preload.onerror = () => { clearTimeout(this.loadTimer); reject(); };
            this.loadTimer = setTimeout(() => reject(new Error('Image load timeout')), 10000);
            if (slide.srcset) preload.srcset = slide.srcset;
            preload.src = slide.src;
          });
          if (preload.decode) await preload.decode();
        } catch {
          preload.onload = preload.onerror = null;
          if (this.pendingImage === preload) this.pendingImage = null;
          if (generation !== this.generation) return;
          clearTimeout(this.loadTimer);
          this.failed.add(candidate);
          continue;
        }
        if (generation !== this.generation || !this.canRun()) return;
        this.pendingImage = null;
        this.nextImage.sizes = this.primary.sizes || '100vw';
        this.applyImage(this.nextImage, slide);
        // Decode the actual overlay too before starting the crossfade.
        try { if (this.nextImage.decode) await this.nextImage.decode(); }
        catch { if (generation !== this.generation) return; this.failed.add(candidate); continue; }
        if (generation !== this.generation || !this.canRun()) return;
        if (this.nextCaption) this.nextCaption.textContent = slide.title || '';
        this.fadingIndex = candidate;
        this.classList.add('lc-category-slideshow--fading');
        this.fadeTimer = setTimeout(() => {
          if (generation !== this.generation) return;
          this.commit();
          this.refresh();
        }, this.motion.matches ? 0 : 600);
        return;
      }
      this.refresh();
    }
  }

  if (!customElements.get('lc-category-slideshow')) customElements.define('lc-category-slideshow', LoveCashmereCategorySlideshow);
  customElements.define('lc-home-rotation', LoveCashmereHomeRotation);
})();
